import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { Company, Pilot, PilotStatus } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { useAsync } from '@/lib/useAsync';

const ACTIVE_STATUSES: readonly PilotStatus[] = [
  'draft',
  'baseline',
  'active',
  'interim_review',
  'final_review',
];

/**
 * "Organisatiegezondheid" at platform level (PLAN-FASE2.md WP-07): a
 * one-card summary above the Companies list — how many companies, their
 * edition split, and how many have a pilot currently running. Composed
 * entirely from `GET /companies` (already fetched by the list itself) and
 * WP-04's `GET /pilots` (no `company_id` — the platform-wide variant this WP
 * adds), no new data source.
 */
export function CompaniesHealthSummaryCard({ companies }: { companies: readonly Company[] }) {
  const { t } = useTranslation();
  const pilots = useAsync(() => apiGet('/pilots') as Promise<Pilot[]>, []);

  const fitCount = companies.filter((company) => (company.edition ?? 'fit') === 'fit').length;
  const soccerCount = companies.filter((company) => company.edition === 'soccer').length;

  return (
    <Card aria-label={t('companies.list.healthTitle')}>
      <CardHeader title={t('companies.list.healthTitle')} />
      <div className="flex flex-wrap items-center gap-6 text-sm">
        <div>
          <p className="text-accent">{t('companies.list.healthCompanies')}</p>
          <p className="text-ink mt-1 text-lg font-semibold">{companies.length}</p>
        </div>
        <div>
          <p className="text-accent">{t('companies.list.healthEditions')}</p>
          <p className="text-ink mt-1 flex gap-2">
            <Badge tone="outline">
              {t('companies.health.editionFit')}: {fitCount}
            </Badge>
            <Badge tone="outline">
              {t('companies.health.editionSoccer')}: {soccerCount}
            </Badge>
          </p>
        </div>
        <div>
          <p className="text-accent">{t('companies.list.healthPilots')}</p>
          <AsyncSection
            state={pilots}
            label={t('companies.list.healthPilotsLabel')}
            skeleton="lines"
          >
            {(rows) => {
              const companiesWithActivePilot = new Set(
                rows
                  .filter((pilot) => ACTIVE_STATUSES.includes(pilot.status as PilotStatus))
                  .map((pilot) => pilot.company_id),
              ).size;
              return (
                <p className="mt-1">
                  <Badge tone={companiesWithActivePilot > 0 ? 'mint' : 'outline'}>
                    {t('companies.list.healthActivePilotCount', {
                      count: companiesWithActivePilot,
                    })}
                  </Badge>{' '}
                  <Link to="/manage/pilots" className="text-green ml-2">
                    {t('companies.health.viewAllPilots')}
                  </Link>
                </p>
              );
            }}
          </AsyncSection>
        </div>
      </div>
    </Card>
  );
}
